import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

// A4 dimensions in mm
const PAGE_WIDTH = 210
const PAGE_HEIGHT = 297
const MARGIN_TOP = 20
const MARGIN_BOTTOM = 18
const MARGIN_SIDE = 10
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_SIDE * 2
const CONTENT_HEIGHT = PAGE_HEIGHT - MARGIN_TOP - MARGIN_BOTTOM
const PAGE_BREAK_SCAN_PX = 180
const MIN_PAGE_FILL_RATIO = 0.72
const ROW_SAMPLE_STEP = 2
const WHITE_THRESHOLD = 245

function sanitizeFilename(name) {
  return name.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-')
}

function formatDate() {
  return new Date().toISOString().split('T')[0]
}

function drawHeader(doc, title, pageWidth) {
  const date = new Date().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
  doc.setFontSize(8)
  doc.setTextColor(27, 58, 45) // primary-800
  doc.text(title, MARGIN_SIDE, 12)
  doc.text(date, pageWidth - MARGIN_SIDE, 12, { align: 'right' })
  doc.setDrawColor(229, 231, 235) // gray-200
  doc.line(MARGIN_SIDE, 15, pageWidth - MARGIN_SIDE, 15)
}

function drawFooter(doc, pageNum, totalPages, pageWidth, pageHeight) {
  doc.setFontSize(7)
  doc.setTextColor(156, 163, 175) // gray-400
  doc.text('Confidential', MARGIN_SIDE, pageHeight - 8)
  doc.text(
    `Page ${pageNum} of ${totalPages}`,
    pageWidth / 2,
    pageHeight - 8,
    { align: 'center' }
  )
}

async function captureElement(element) {
  return html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false,
    windowWidth: 896,
  })
}

function getRowInkRatio(imageData, rowOffset, width) {
  let nonWhitePixels = 0
  let sampledPixels = 0
  const rowStart = rowOffset * width * 4

  for (let x = 0; x < width; x += ROW_SAMPLE_STEP) {
    const idx = rowStart + x * 4
    const alpha = imageData[idx + 3]

    if (alpha > 8) {
      const r = imageData[idx]
      const g = imageData[idx + 1]
      const b = imageData[idx + 2]
      if (r < WHITE_THRESHOLD || g < WHITE_THRESHOLD || b < WHITE_THRESHOLD) {
        nonWhitePixels++
      }
    }
    sampledPixels++
  }

  return sampledPixels === 0 ? 0 : nonWhitePixels / sampledPixels
}

function findSafeBreakY(canvas, ctx, sourceY, idealBreakY, maxBreakY, contentHeightPx) {
  const minBreakY = Math.max(
    sourceY + Math.floor(contentHeightPx * MIN_PAGE_FILL_RATIO),
    idealBreakY - PAGE_BREAK_SCAN_PX
  )
  // Never scan below the ideal boundary; this prevents content from spilling into footer space.
  const scanEndY = Math.min(maxBreakY, idealBreakY)

  if (scanEndY <= minBreakY) return Math.min(idealBreakY, maxBreakY)

  const scanHeight = scanEndY - minBreakY + 1
  const region = ctx.getImageData(0, minBreakY, canvas.width, scanHeight).data

  let bestY = Math.min(idealBreakY, maxBreakY)
  let bestInkRatio = Number.POSITIVE_INFINITY

  for (let y = minBreakY; y <= scanEndY; y++) {
    const rowOffset = y - minBreakY
    const inkRatio = getRowInkRatio(region, rowOffset, canvas.width)
    const isBetterInk = inkRatio < bestInkRatio
    const isCloserAtSameInk = inkRatio === bestInkRatio && Math.abs(y - idealBreakY) < Math.abs(bestY - idealBreakY)

    if (isBetterInk || isCloserAtSameInk) {
      bestInkRatio = inkRatio
      bestY = y
    }

  }

  return bestY
}

function buildCanvasSlices(canvas) {
  const imgWidthPx = canvas.width
  const imgHeightPx = canvas.height

  // How many px per mm of content width
  const pxPerMm = imgWidthPx / CONTENT_WIDTH
  const contentHeightPx = Math.floor(CONTENT_HEIGHT * pxPerMm)
  const minSlicePx = Math.floor(contentHeightPx * MIN_PAGE_FILL_RATIO)
  const slices = []
  const ctx = canvas.getContext('2d', { willReadFrequently: true })

  let sourceY = 0
  while (sourceY < imgHeightPx) {
    const remainingPx = imgHeightPx - sourceY
    if (remainingPx <= contentHeightPx) {
      slices.push({ sourceY, sourceH: remainingPx })
      break
    }

    const idealBreakY = sourceY + contentHeightPx
    const maxBreakY = imgHeightPx - 1
    const selectedBreakY = ctx
      ? findSafeBreakY(canvas, ctx, sourceY, idealBreakY, maxBreakY, contentHeightPx)
      : Math.min(idealBreakY, maxBreakY)

    let breakY = Math.min(Math.max(selectedBreakY, sourceY + 1), maxBreakY)

    // Ensure each page keeps most of its available height.
    if (breakY - sourceY < minSlicePx) {
      breakY = Math.min(sourceY + contentHeightPx, maxBreakY)
    }

    slices.push({ sourceY, sourceH: breakY - sourceY })
    sourceY = breakY
  }

  return { slices, pxPerMm }
}

function addCanvasPages(doc, canvas, { slices, pxPerMm }, title, startPage, totalPages) {
  const imgWidthPx = canvas.width

  for (let i = 0; i < slices.length; i++) {
    if (startPage + i > 1) doc.addPage()

    const { sourceY, sourceH } = slices[i]

    // Create a page-sized canvas slice
    const pageCanvas = document.createElement('canvas')
    pageCanvas.width = imgWidthPx
    pageCanvas.height = sourceH
    const ctx = pageCanvas.getContext('2d')
    ctx.drawImage(canvas, 0, sourceY, imgWidthPx, sourceH, 0, 0, imgWidthPx, sourceH)

    const imgData = pageCanvas.toDataURL('image/png')
    const sliceHeightMm = sourceH / pxPerMm

    // Keep embedded image lossless to preserve subtle highlight colors.
    doc.addImage(imgData, 'PNG', MARGIN_SIDE, MARGIN_TOP, CONTENT_WIDTH, sliceHeightMm, undefined, 'NONE')

    drawHeader(doc, title, PAGE_WIDTH)
    drawFooter(doc, startPage + i, totalPages, PAGE_WIDTH, PAGE_HEIGHT)
  }

  return slices.length
}

/**
 * Find which page a canvas-pixel Y coordinate falls on, and its Y offset within that page (in mm).
 */
function resolvePagePosition(canvasY, slices, pxPerMm) {
  for (let i = 0; i < slices.length; i++) {
    const { sourceY, sourceH } = slices[i]
    if (canvasY >= sourceY && canvasY < sourceY + sourceH) {
      return { page: i + 1, yMm: MARGIN_TOP + (canvasY - sourceY) / pxPerMm }
    }
  }
  // Falls on/after the last page
  const last = slices[slices.length - 1]
  return { page: slices.length, yMm: MARGIN_TOP + (canvasY - last.sourceY) / pxPerMm }
}

/**
 * Add internal link annotations to the PDF.
 * Each link has { x, y, w, h } in DOM pixels (the clickable area)
 * and { targetY } in DOM pixels (the scroll destination).
 * xScale/yScale convert DOM pixels to canvas pixels (may differ if
 * html2canvas renders at a different height than the DOM reports).
 */
function addInternalLinks(doc, internalLinks, slices, pxPerMm, xScale, yScale, startPage) {
  for (const link of internalLinks) {
    // Convert DOM pixels to canvas pixels using actual scales
    const srcX = link.x * xScale
    const srcY = link.y * yScale
    const srcW = link.w * xScale
    const srcH = link.h * yScale
    const tgtY = link.targetY * yScale

    const src = resolvePagePosition(srcY, slices, pxPerMm)
    const tgt = resolvePagePosition(tgtY, slices, pxPerMm)

    const xMm = MARGIN_SIDE + srcX / pxPerMm
    const wMm = srcW / pxPerMm
    const hMm = srcH / pxPerMm

    doc.setPage(startPage + src.page - 1)
    doc.link(xMm, src.yMm, wMm, hMm, {
      pageNumber: startPage + tgt.page - 1,
      top: tgt.yMm,
    })
  }
}

/**
 * Generate a multi-page PDF from a single DOM element.
 * Optional internalLinks: array of { x, y, w, h, targetY } in DOM pixels.
 */
export async function generatePDF(element, { title, filename, internalLinks, domWidth, domHeight }) {
  const canvas = await captureElement(element)
  const sliced = buildCanvasSlices(canvas)
  const totalPages = sliced.slices.length

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  addCanvasPages(doc, canvas, sliced, title, 1, totalPages)

  if (internalLinks && internalLinks.length > 0) {
    // Compute actual DOM→canvas scale factors; html2canvas may render at a
    // different height than the DOM reports (font/CSS differences in the clone).
    const xScale = domWidth ? canvas.width / domWidth : 2
    const yScale = domHeight ? canvas.height / domHeight : 2
    addInternalLinks(doc, internalLinks, sliced.slices, sliced.pxPerMm, xScale, yScale, 1)
  }

  doc.save(filename)
}

/**
 * Generate a combined PDF from two DOM elements (scores + report).
 */
export async function generateCombinedPDF(scoresElement, reportElement, { title, filename }) {
  const [scoresCanvas, reportCanvas] = await Promise.all([
    captureElement(scoresElement),
    captureElement(reportElement),
  ])

  const scoresSliced = buildCanvasSlices(scoresCanvas)
  const reportSliced = buildCanvasSlices(reportCanvas)
  const scoresPageCount = scoresSliced.slices.length
  const reportPageCount = reportSliced.slices.length
  const totalPages = scoresPageCount + reportPageCount

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  addCanvasPages(doc, scoresCanvas, scoresSliced, title, 1, totalPages)
  addCanvasPages(doc, reportCanvas, reportSliced, title, scoresPageCount + 1, totalPages)
  doc.save(filename)
}

/**
 * Build a standardized filename for PDF export.
 */
export function buildPdfFilename(type, name1, name2) {
  const n1 = sanitizeFilename(name1)
  const n2 = sanitizeFilename(name2)
  const date = formatDate()
  return `csi-${type}-${n1}-${n2}-${date}.pdf`
}
